import React from "react";
import PropTypes from "prop-types";
import { css } from "@emotion/react";
import { FormattedMessage } from "react-intl";
import { ModalForm, MODAL_SIZE } from "components/Modal";
import { SIZE, TYPE } from "components/Form";
import { FEATURE_ENTITLEMENTS } from "constants/index";

/**
 * UserFeatureEntitlementsForm
 *
 * Admin-only control over which simplified-portal features a customer user gets.
 * Writes through SetFeatureEntitlements rather than UpdateUser, because UpdateUser
 * is reachable by an organization's primary contact.
 *
 * @param {Boolean}   show
 * @param {Function}  onClose
 * @param {Object}    userData
 * @param {Function}  refetch
 * @param {Function}  onSave
 * @param {Boolean}   saving
 */
const UserFeatureEntitlementsForm = ({ show, onClose, userData, refetch, onSave, saving, ...props }) => {
  const granted = userData?.featureEntitlements || [];
  const available = userData?.availableFeatureEntitlements || [];
  const features = FEATURE_ENTITLEMENTS.filter(({ id }) => available.includes(id));
  const initialValues = features.reduce((acc, { id }) => ({ ...acc, [id]: granted.includes(id) }), {});

  const handleSuccess = () => {
    refetch?.();
    onClose();
  };

  const handleSubmit = (formData) =>
    onSave(
      userData?.id,
      features.filter(({ id }) => formData[id]).map(({ id }) => id),
      handleSuccess
    );

  return (
    <ModalForm
      title={
        <FormattedMessage id="Settings.Users.UserFeatureEntitlementsForm.Title" values={{ name: userData?.name }} />
      }
      description={<FormattedMessage id="Settings.Users.UserFeatureEntitlementsForm.Description" />}
      data={FORM_DATA(features)}
      initialValues={initialValues}
      onSubmit={handleSubmit}
      loading={saving}
      show={show}
      size={MODAL_SIZE.medium}
      onClose={onClose}
      {...props}
    />
  );
};

const FORM_DATA = (features) => [
  {
    style: css`
      width: 100%;
    `,
    items: features.map(({ id, labelId }) => ({
      type: TYPE.checkbox,
      size: SIZE.xlarge,
      properties: {
        name: id,
        label: <FormattedMessage id={labelId} />,
      },
    })),
  },
];

UserFeatureEntitlementsForm.defaultProps = {
  show: false,
  onClose: () => {},
  userData: {},
  refetch: () => {},
  onSave: () => {},
  saving: false,
};

UserFeatureEntitlementsForm.propTypes = {
  show: PropTypes.bool,
  onClose: PropTypes.func,
  userData: PropTypes.object,
  refetch: PropTypes.func,
  onSave: PropTypes.func,
  saving: PropTypes.bool,
};

export default UserFeatureEntitlementsForm;
